import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AuthLayout from '../components/Layout/AuthLayout';

/**
 * StudentLoginPage - Login form for students (COPPA compliant - username only, no email)
 * Connects to backend /api/auth/student/login endpoint
 */
const StudentLoginPage = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { loginStudent } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!username || !password) {
      setError('Please fill in all required fields');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      await loginStudent(username, password);
      // Redirect to dashboard (student's main interface)
      navigate('/student/dashboard');
    } catch (err) {
      setError(err.message || 'Login failed. Please check your username and password.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthLayout showBackButton={true}>
      <form className="w-full" onSubmit={handleSubmit}>
        {/* Icon & Title */}
        <div className="text-center mb-8">
          <div className="relative inline-block mb-6">
            {/* Icon Glow */}
            <div className="absolute inset-0 blur-3xl opacity-40 bg-violet-500" />
            {/* Icon Container */}
            <div className="relative bg-gradient-to-br from-violet-500 to-purple-600 rounded-[1.5rem] p-6 shadow-2xl">
              <svg className="w-12 h-12 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14zm-4 6v-7.5l4-2.222" />
              </svg>
            </div>
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-2 tracking-tight">
            Student Login
          </h2>
          <p className="text-gray-500 text-base">
            Welcome back! Let's continue learning
          </p>
        </div>

        {/* Form Fields */}
        <div className="space-y-5 mb-6">
          <div className="form-group">
            <label htmlFor="student-username" className="block mb-2 font-semibold text-gray-800 text-sm">
              Username*
            </label>
            <input
              type="text"
              id="student-username"
              placeholder="your_username"
              className="w-full py-3 px-4 border-2 border-gray-200 rounded-xl bg-white focus:outline-none focus:border-violet-500 transition-colors"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              disabled={isLoading}
              autoComplete="username"
            />
          </div>

          <div className="form-group">
            <label htmlFor="student-password" className="block mb-2 font-semibold text-gray-800 text-sm">
              Password*
            </label>
            <input
              type="password"
              id="student-password"
              placeholder="Enter password"
              className="w-full py-3 px-4 border-2 border-gray-200 rounded-xl bg-white focus:outline-none focus:border-violet-500 transition-colors"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={isLoading}
              autoComplete="current-password"
            />
          </div>
        </div>

        <button
          type="submit"
          className={`w-full bg-gradient-to-r from-violet-600 to-purple-700 text-white font-semibold py-4 px-6 rounded-2xl
                     hover:shadow-2xl hover:scale-[1.02] transition-all duration-300 text-base tracking-wide mb-4 ${
            isLoading ? 'opacity-70 cursor-not-allowed' : ''
          }`}
          disabled={isLoading}
        >
          {isLoading ? 'Signing In...' : 'Sign In'}
        </button>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border-2 border-red-200 rounded-xl text-red-700 text-sm text-center">
            {error}
          </div>
        )}

        {/* Divider */}
        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-200"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-4 bg-white text-gray-400">New student?</span>
          </div>
        </div>

        <Link
          to="/register/student"
          className="block text-center w-full bg-gray-100 text-gray-800 font-semibold py-4 px-6 rounded-2xl
                   hover:bg-gray-200 hover:shadow-lg hover:scale-[1.02] transition-all duration-300 text-base"
        >
          Register with activation code
        </Link>
      </form>
    </AuthLayout>
  );
};

export default StudentLoginPage;
